import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
} from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { openPdfDocument, withPdfSlot } from '@/lib/pdfjs';

/** A4 portrait — ratio d'attente avant que la vraie page soit mesurée. */
const DEFAULT_ASPECT = 210 / 297;

/** Une page ne prend JAMAIS toute la largeur d'un grand écran : à 1600 px de
 *  large, un A4 devient une affiche, le corps de texte fait 30 px et il faut
 *  balayer la tête de gauche à droite pour lire une ligne. On plafonne à une
 *  largeur de lecture confortable, et le zoom prend le relais au-delà. */
const MAX_PAGE_WIDTH = 900;
const MAX_PAGE_WIDTH_FULLSCREEN = 1200;

/** Paliers de zoom, 1 = largeur de lecture ci-dessus. */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
const ZOOM_DEFAULT = 1;

/** Hauteur de la barre d'outils collante — sert de marge de défilement pour
 *  qu'un « aller à la page » ne glisse pas la page sous la barre. */
const TOOLBAR_OFFSET = 60;

interface Props {
  url: string;
  /** Remonté quand le document est illisible : la page appelante bascule
   *  alors sur « Ouvrir / Télécharger ». */
  onFailed?: () => void;
}

/**
 * Le document entier, page après page, avec sa barre d'outils.
 *
 * Toutes les pages sont dans le flux dès le départ (à la bonne hauteur, donc
 * la barre de défilement est juste), mais seules celles qui approchent de
 * l'écran sont rasterisées — et leur bitmap est rendu au navigateur dès
 * qu'elles s'en éloignent. Un catalogue de 200 pages tient ainsi dans la
 * mémoire d'un téléphone.
 */
export function PdfDocumentView({ url, onFailed }: Props) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const [frameWidth, setFrameWidth] = useState(0);
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);
  const [current, setCurrent] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  // Gardée dans une ref : la page appelante recrée la fonction à chaque rendu,
  // la mettre en dépendance relancerait le chargement en boucle.
  const onFailedRef = useRef(onFailed);
  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  useEffect(() => {
    let cancelled = false;
    let opened: PDFDocumentProxy | null = null;

    setDoc(null);
    setLoading(true);
    setCurrent(1);
    setZoom(ZOOM_DEFAULT);

    openPdfDocument(url)
      .then(async (loaded) => {
        // Démonté pendant le chargement : le nettoyage est déjà passé sans
        // rien à fermer, c'est donc ici qu'il faut rendre le document.
        if (cancelled) {
          void loaded.destroy();
          return;
        }
        opened = loaded;
        // Le format de la première page sert de gabarit aux suivantes : elles
        // occupent la bonne hauteur avant même d'être rendues.
        const first = await loaded.getPage(1);
        const viewport = first.getViewport({ scale: 1 });
        if (cancelled) return;
        setAspect(viewport.width / viewport.height);
        setDoc(loaded);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        onFailedRef.current?.();
      });

    return () => {
      cancelled = true;
      void opened?.destroy();
    };
  }, [url]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setFrameWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const pages = useMemo(
    () => (doc ? Array.from({ length: doc.numPages }, (_, i) => i + 1) : []),
    [doc],
  );

  const handleVisible = useCallback((pageNumber: number) => {
    setCurrent(pageNumber);
  }, []);

  const goToPage = useCallback(
    (pageNumber: number, smooth: boolean) => {
      const target = Math.min(Math.max(pageNumber, 1), pages.length || 1);
      const el = frameRef.current?.querySelector(`[data-page="${target}"]`);
      el?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'start',
      });
      setCurrent(target);
    },
    [pages.length],
  );

  const stepZoom = (direction: 1 | -1) => {
    setZoom((z) => {
      const index = ZOOM_STEPS.indexOf(z);
      const next = (index === -1 ? ZOOM_STEPS.indexOf(ZOOM_DEFAULT) : index) + direction;
      return ZOOM_STEPS[Math.min(Math.max(next, 0), ZOOM_STEPS.length - 1)] ?? z;
    });
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void rootRef.current?.requestFullscreen?.();
    }
  };

  const maxWidth = fullscreen ? MAX_PAGE_WIDTH_FULLSCREEN : MAX_PAGE_WIDTH;
  const baseWidth = Math.min(frameWidth, maxWidth);
  const pageWidth = Math.max(0, Math.round(baseWidth * zoom));
  const canZoomOut = zoom > (ZOOM_STEPS[0] ?? 0.5);
  const canZoomIn = zoom < (ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? 3);

  return (
    <div
      ref={rootRef}
      className={
        fullscreen
          ? 'h-screen overflow-y-auto px-3 pb-6'
          : 'relative'
      }
      style={fullscreen ? { background: 'var(--pro-bg, #fafafa)' } : undefined}
    >
      {!loading && doc && (
        <Toolbar
          width={pageWidth}
          current={current}
          total={pages.length}
          zoom={zoom}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
          fullscreen={fullscreen}
          onGoToPage={goToPage}
          onZoomIn={() => stepZoom(1)}
          onZoomOut={() => stepZoom(-1)}
          onZoomReset={() => setZoom(ZOOM_DEFAULT)}
          onToggleFullscreen={toggleFullscreen}
        />
      )}

      <div ref={frameRef} className="overflow-x-auto">
        {loading ? (
          <div
            className="w-full rounded-2xl flex items-center justify-center bg-white mx-auto"
            style={{
              maxWidth: MAX_PAGE_WIDTH,
              aspectRatio: String(DEFAULT_ASPECT),
              maxHeight: '78vh',
              border: '1px solid var(--pro-border)',
            }}
          >
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-500" />
          </div>
        ) : (
          <div
            className="mx-auto flex flex-col items-center gap-3"
            style={{ width: pageWidth || undefined }}
          >
            {/* Tant que la largeur n'est pas mesurée, les cadres seraient
                plats : toutes les pages se croiraient à l'écran et se
                mettraient en file de rendu d'un coup. */}
            {doc &&
              pageWidth > 0 &&
              pages.map((pageNumber) => (
                <PdfPage
                  key={pageNumber}
                  doc={doc}
                  pageNumber={pageNumber}
                  width={pageWidth}
                  fallbackAspect={aspect}
                  onVisible={handleVisible}
                  inWindow={Math.abs(pageNumber - current) <= 1}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Barre d'outils
// ------------------------------------------------------------------

interface ToolbarProps {
  /** Largeur de la colonne de pages : la barre se cale dessus plutôt que de
   *  s'étaler sur tout l'écran, avec un plancher pour rester lisible. */
  width: number;
  current: number;
  total: number;
  zoom: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  fullscreen: boolean;
  onGoToPage: (pageNumber: number, smooth: boolean) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onToggleFullscreen: () => void;
}

function Toolbar({
  width,
  current,
  total,
  zoom,
  canZoomIn,
  canZoomOut,
  fullscreen,
  onGoToPage,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onToggleFullscreen,
}: ToolbarProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(String(current));
  const [editing, setEditing] = useState(false);

  // Le champ suit le défilement, sauf pendant qu'on le remplit.
  useEffect(() => {
    if (!editing) setDraft(String(current));
  }, [current, editing]);

  const commit = () => {
    setEditing(false);
    const parsed = Number.parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(current));
      return;
    }
    // Saut potentiellement long : pas de défilement animé, on y va direct.
    onGoToPage(parsed, false);
  };

  // Le plein écran n'existe pas sur iOS Safari (réservé aux vidéos) : on ne
  // montre pas un bouton qui ne ferait rien.
  const canFullscreen =
    typeof document !== 'undefined' && document.fullscreenEnabled;

  return (
    <div
      className="sticky top-0 z-10 py-2"
      style={{ background: 'var(--pro-bg, #fafafa)' }}
    >
      <div
        className="mx-auto flex items-center justify-between gap-2 rounded-xl bg-white h-11 px-1.5"
        style={{
          maxWidth: Math.max(width, 360),
          border: '1px solid var(--pro-border)',
        }}
      >
        <div
          className="flex items-center gap-0.5"
          role="group"
          aria-label={t(
            'pro.catalogue.page_status',
            'Page {{current}} sur {{total}}',
            { current, total },
          )}
        >
          <ToolbarButton
            label={t('pro.catalogue.prev_page', 'Page précédente')}
            disabled={current <= 1}
            onClick={() => onGoToPage(current - 1, true)}
          >
            <ChevronUp className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            label={t('pro.catalogue.next_page', 'Page suivante')}
            disabled={current >= total}
            onClick={() => onGoToPage(current + 1, true)}
          >
            <ChevronDown className="w-4 h-4" />
          </ToolbarButton>
          <input
            value={draft}
            onChange={(e) => {
              setEditing(true);
              setDraft(e.target.value.replace(/[^0-9]/g, ''));
            }}
            onFocus={() => setEditing(true)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            inputMode="numeric"
            aria-label={t('pro.catalogue.go_to_page', 'Aller à la page')}
            className="w-10 h-8 ms-1 text-center rounded-lg text-[13px] font-semibold tabular-nums outline-none focus:ring-2 focus:ring-neutral-200"
            style={{
              border: '1px solid var(--pro-border)',
              color: 'var(--pro-text-dark)',
              fontFamily: 'Figtree',
            }}
          />
          <span
            className="ps-1.5 pe-1 text-[13px] font-semibold tabular-nums whitespace-nowrap"
            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
          >
            / {total}
          </span>
        </div>

        <div className="flex items-center gap-0.5">
          <ToolbarButton
            label={t('pro.catalogue.zoom_out', 'Réduire')}
            disabled={!canZoomOut}
            onClick={onZoomOut}
          >
            <Minus className="w-4 h-4" />
          </ToolbarButton>
          <button
            type="button"
            onClick={onZoomReset}
            title={t('pro.catalogue.zoom_reset', 'Taille par défaut')}
            className="hidden sm:inline-flex h-8 px-2 items-center justify-center rounded-lg text-[13px] font-semibold tabular-nums hover:bg-neutral-100 transition-colors"
            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
          >
            {Math.round(zoom * 100)}%
          </button>
          <ToolbarButton
            label={t('pro.catalogue.zoom_in', 'Agrandir')}
            disabled={!canZoomIn}
            onClick={onZoomIn}
          >
            <Plus className="w-4 h-4" />
          </ToolbarButton>
          {canFullscreen && (
            <ToolbarButton
              label={
                fullscreen
                  ? t('pro.catalogue.exit_fullscreen', 'Quitter le plein écran')
                  : t('pro.catalogue.fullscreen', 'Plein écran')
              }
              onClick={onToggleFullscreen}
            >
              {fullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </ToolbarButton>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolbarButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-neutral-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
      style={{ color: 'var(--pro-text-dark)' }}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------
// Une page
// ------------------------------------------------------------------

interface PageProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  /** Largeur CSS cible, zoom compris. */
  width: number;
  fallbackAspect: number;
  onVisible: (pageNumber: number) => void;
  /** Page voisine immédiate de celle qu'on regarde. Doublure du détecteur de
   *  proximité : la marge d'un IntersectionObserver s'applique à la fenêtre du
   *  navigateur, pas au conteneur défilant intermédiaire (`main` dans la
   *  coquille de l'Espace Pro), donc l'anticipation y serait rognée. */
  inWindow: boolean;
}

const PdfPage = memo(function PdfPage({
  doc,
  pageNumber,
  width,
  fallbackAspect,
  onVisible,
  inWindow,
}: PageProps) {
  const holderRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [aspect, setAspect] = useState(fallbackAspect);
  const [near, setNear] = useState(false);
  const [painted, setPainted] = useState(false);
  const active = near || inWindow;

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    // Une page et demie d'avance de chaque côté : le rendu a le temps de
    // finir avant que la page n'entre dans le champ.
    const nearObserver = new IntersectionObserver(
      (entries) => setNear(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: '150% 0px' },
    );
    nearObserver.observe(el);

    // Bande étroite au milieu de l'écran : la page qui la croise est celle
    // que l'utilisateur regarde, donc celle qu'affiche le compteur.
    const centerObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible(pageNumber);
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    );
    centerObserver.observe(el);

    return () => {
      nearObserver.disconnect();
      centerObserver.disconnect();
    };
  }, [onVisible, pageNumber]);

  useEffect(() => {
    if (!active || width <= 0) return;

    let cancelled = false;
    let task: RenderTask | null = null;

    const job = withPdfSlot(async () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const page = await doc.getPage(pageNumber);
      if (cancelled) return;

      const base = page.getViewport({ scale: 1 });
      setAspect(base.width / base.height);
      // Plafonné à 2× : au-delà, la mémoire du canvas explose sur mobile pour
      // un gain de netteté invisible.
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: (width * ratio) / base.width });

      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);

      task = page.render({ canvasContext: context, viewport });
      await task.promise;
      if (!cancelled) setPainted(true);
    });

    // Annulation de rendu ou page illisible : le cadre reste vide, le reste
    // du document continue de s'afficher.
    job.catch(() => undefined);

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [active, doc, pageNumber, width]);

  useEffect(() => {
    if (active) return;
    // iOS plafonne la mémoire canvas d'un onglet : on rend le bitmap au
    // navigateur dès que la page s'est éloignée pour de bon.
    const timer = window.setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = 0;
      canvas.height = 0;
      setPainted(false);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [active]);

  return (
    <div
      ref={holderRef}
      data-page={pageNumber}
      className="relative w-full rounded-xl overflow-hidden bg-white"
      style={{
        aspectRatio: String(aspect),
        border: '1px solid var(--pro-border)',
        scrollMarginTop: TOOLBAR_OFFSET,
      }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
      {!painted && (
        <div
          className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold"
          style={{
            background: 'var(--pro-grey-subtle, #f7f7f7)',
            color: 'var(--pro-text-grey-light)',
            fontFamily: 'Figtree',
          }}
        >
          {pageNumber}
        </div>
      )}
    </div>
  );
});
